import { Container, SpinnerWrapper } from './index.styles';
import { Button, ButtonProps } from '../Button';
import { Checkbox } from '../Checkbox';
import { Text } from '../../atoms/Text';
import { Tooltip } from '../../atoms/Tooltip';
import { Spinner } from '../../atoms/Spinner';
import { generateId } from '../../utils/helpers';

type ActionButtonProps = Required<Pick<ButtonProps, 'icon' | 'label'>> &
  Pick<ButtonProps, 'disabled' | 'size' | 'title'> & { action: string; isDisabled?: boolean };

export interface BulkActionsBarProps extends Component {
  /**
   * Action click event emitter. Optional.
   */
  act?: (action: string) => void;
  /**
   * Array of action buttons. Optional.
   */
  actionButtons?: ActionButtonProps[];
  /**
   * Visual appearance. 'floating' renders a fixed, bottom-centered dark bar with enter/exit motion. Optional, defaults to 'inline'.
   */
  appearance?: 'inline' | 'floating';
  /**
   * Deselect items event emitter. Required.
   */
  deselectAll: () => void;
  /**
   * Explicitly marks the selection as "everything" for the checkbox's checked/indeterminate
   * state, instead of deriving it from `selectedItemsCount === maxItemsCount`. Prefer this
   * when those two counts can come from different sources and may not agree exactly (e.g.
   * a summary count vs. an actual fetched id list) — a mismatch would otherwise strand the
   * checkbox in "indeterminate" forever, making it impossible to ever reach deselect-all.
   * Optional.
   */
  isAllSelected?: boolean;
  /**
   * Whether selectAll is fetching the full set (e.g. every post in a month) — swaps the
   * select-all checkbox for a spinner and disables it while true. Optional.
   */
  isSelectAllLoading?: boolean;
  /**
   * List total amount of items. Required.
   */
  maxItemsCount: number;
  /**
   * Select items event emitter. Required.
   */
  selectAll: () => void;
  /**
   * Number of selected items. Required.
   */
  selectedItemsCount: number;
  /**
   * Text for selected items. Required.
   */
  selectedItemsLabel: string;
}

/**
 * BulkActionsBar
 */
export const BulkActionsBar = (props: BulkActionsBarProps) => {
  const {
    act,
    actionButtons,
    appearance = 'inline',
    className = '',
    customClass = '',
    deselectAll,
    isAllSelected,
    isSelectAllLoading = false,
    maxItemsCount,
    selectAll,
    selectedItemsCount,
    selectedItemsLabel,
    testId = 'bulk-actions-bar',
  } = props;

  const allSelected = isAllSelected ?? selectedItemsCount >= maxItemsCount;
  const fillState = selectedItemsCount === 0 ? 'unchecked' : allSelected ? 'checked' : 'indeterminate';
  const isFloating = appearance === 'floating';

  const floatingMotionProps = isFloating
    ? {
        initial: { y: 24, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: 24, opacity: 0 },
        transition: { ease: 'easeInOut', duration: 0.3 } as const,
      }
    : {};

  return (
    <Container
      className={`bulk-actions-bar ${customClass} ${className}`}
      data-testid={testId}
      $appearance={appearance}
      {...floatingMotionProps}
    >
      {isSelectAllLoading ? (
        <SpinnerWrapper>
          <Spinner
            size="xs"
            testId={`${testId}-checkbox-loading`}
          />
        </SpinnerWrapper>
      ) : (
        <Checkbox
          check={() => (fillState !== 'checked' ? selectAll() : deselectAll())}
          initialState={fillState}
          testId={`${testId}-checkbox`}
        />
      )}
      <Text
        align="left"
        color={isFloating ? 'brandLight' : 'brandDark'}
        variation="body_12"
        testId="checkbox-bulk-bar-label"
      >{`${selectedItemsCount} ${selectedItemsLabel}`}</Text>
      {actionButtons &&
        actionButtons.map(({ action, icon, isDisabled, label }) => (
          <Tooltip
            key={generateId()}
            content={<>{label}</>}
          >
            <Button
              onClick={() => act && act(action)}
              icon={icon}
              disabled={isDisabled}
              darkmode={isFloating}
              key={generateId()}
              size={14}
              data-testid={`${testId}-button-${icon}`}
            />
          </Tooltip>
        ))}
    </Container>
  );
};
